import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { computeOrder, formatHoursAsDH, formatNum, grainTypeMeta } from "@/lib/growthLogic";
import useTicker from "@/hooks/useTicker";

const META = {
  active: {
    title: "Active Orders",
    description: "All orders currently in production.",
    accent: "border-emerald-200 bg-emerald-50/40",
  },
  trays: {
    title: "Trays In Use",
    description: "Trays committed to active orders.",
    accent: "border-teal-200 bg-teal-50/40",
  },
  grain: {
    title: "Grain Required",
    description: "Grain weight reserved across active orders.",
    accent: "border-amber-200 bg-amber-50/40",
  },
  harvest: {
    title: "Harvest ≤ 24h",
    description: "Orders ready within the next day.",
    accent: "border-rose-200 bg-rose-50/40",
  },
};

export function MetricDetailDialog({ kind, orders, open, onOpenChange, roundTrays, onRowClick }) {
  const now = useTicker(60_000);
  if (!kind) return null;
  const meta = META[kind];

  const enriched = orders.map((o) => ({ order: o, calc: computeOrder(o, now) }));

  let rows = enriched;
  if (kind === "harvest") {
    rows = enriched
      .filter(({ calc }) => calc.hoursLeft <= 24 && calc.hoursLeft > 0)
      .sort((a, b) => a.calc.hoursLeft - b.calc.hoursLeft);
  } else {
    rows = enriched.sort((a, b) => b.calc.hoursInProduction - a.calc.hoursInProduction);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-[760px] max-h-[92vh] overflow-hidden flex flex-col ${meta.accent}`}
        data-testid={`metric-detail-${kind}`}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">{meta.title}</DialogTitle>
          <DialogDescription>
            {meta.description} · Showing {rows.length} {rows.length === 1 ? "entry" : "entries"}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto -mx-6 px-6">
          {rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-stone-500">
              Nothing to show right now.
            </div>
          ) : (
            <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50/80 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
                  <DetailHeader kind={kind} />
                </thead>
                <tbody>
                  {rows.map(({ order, calc }) => (
                    <DetailRow
                      key={order.id}
                      kind={kind}
                      order={order}
                      calc={calc}
                      onClick={() => {
                        onOpenChange(false);
                        onRowClick?.(order);
                      }}
                    />
                  ))}
                </tbody>
                {rows.length > 1 && <DetailFooter kind={kind} rows={rows} />}
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-3 py-2.5 font-semibold ${className}`}>{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 align-middle ${className}`}>{children}</td>;
}

function DetailHeader({ kind }) {
  switch (kind) {
    case "active":
      return (
        <tr>
          <Th>Customer</Th><Th>Grain</Th><Th>Order</Th><Th>Day</Th><Th>Phase</Th>
        </tr>
      );
    case "trays":
      return (
        <tr>
          <Th>Customer</Th><Th>Order kg</Th><Th>Trays (exact)</Th><Th>Trays (rnd)</Th><Th>Yield/tray</Th>
        </tr>
      );
    case "grain":
      return (
        <tr>
          <Th>Customer</Th><Th>Grain Type</Th><Th>Order kg</Th><Th>Grain Required</Th>
        </tr>
      );
    case "harvest":
      return (
        <tr>
          <Th>Customer</Th><Th>Grain</Th><Th>Hours Left</Th><Th>Delivery Date</Th>
        </tr>
      );
    default:
      return null;
  }
}

function GrainChip({ value }) {
  const g = grainTypeMeta(value);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${g.chip}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${g.dot}`} />
      {g.label}
    </span>
  );
}

function CustomerCell({ order, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left font-medium text-stone-900 hover:text-emerald-700 underline-offset-2 hover:underline"
      data-testid={`detail-row-${order.id}`}
    >
      {order.customer_name}
    </button>
  );
}

function DetailRow({ kind, order, calc, onClick }) {
  const stage = calc.stage;
  const dayNum = stage.day;
  switch (kind) {
    case "active":
      return (
        <tr className="border-t border-stone-100">
          <Td><CustomerCell order={order} onClick={onClick} /></Td>
          <Td><GrainChip value={order.grain_type} /></Td>
          <Td className="tabular-nums">{formatNum(order.order_quantity_kg, 2)} kg</Td>
          <Td className="tabular-nums">{dayNum} / 7</Td>
          <Td className="text-stone-700">{calc.overdue ? "Overdue" : stage.label.replace(/^Day \d+: /, "")}</Td>
        </tr>
      );
    case "trays": {
      const tw = order.target_weight_per_tray;
      return (
        <tr className="border-t border-stone-100">
          <Td><CustomerCell order={order} onClick={onClick} /></Td>
          <Td className="tabular-nums">{formatNum(order.order_quantity_kg, 2)}</Td>
          <Td className="tabular-nums">{formatNum(calc.traysNeededRaw, 2)}</Td>
          <Td className="tabular-nums font-medium">{calc.traysNeededRounded}</Td>
          <Td className="tabular-nums text-stone-500">{tw} kg</Td>
        </tr>
      );
    }
    case "grain":
      return (
        <tr className="border-t border-stone-100">
          <Td><CustomerCell order={order} onClick={onClick} /></Td>
          <Td><GrainChip value={order.grain_type} /></Td>
          <Td className="tabular-nums">{formatNum(order.order_quantity_kg, 2)}</Td>
          <Td className="tabular-nums font-medium">{formatNum(calc.grainNeeded, 2)} kg</Td>
        </tr>
      );
    case "harvest": {
      const delivery = new Date(
        new Date(order.start_datetime).getTime() + order.standard_cycle_days * 86400000,
      ).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
      return (
        <tr className="border-t border-stone-100">
          <Td><CustomerCell order={order} onClick={onClick} /></Td>
          <Td><GrainChip value={order.grain_type} /></Td>
          <Td className="tabular-nums font-mono">{formatHoursAsDH(calc.hoursLeft)}</Td>
          <Td className="font-mono text-stone-700">{delivery}</Td>
        </tr>
      );
    }
    default:
      return null;
  }
}

function DetailFooter({ kind, rows }) {
  if (kind === "trays") {
    const sumExact = rows.reduce((s, { calc }) => s + calc.traysNeededRaw, 0);
    const sumRnd = rows.reduce((s, { calc }) => s + calc.traysNeededRounded, 0);
    return (
      <tfoot>
        <tr className="border-t-2 border-stone-200 bg-stone-50/80">
          <td className="px-3 py-2.5 font-semibold text-stone-700">Total</td>
          <td />
          <td className="px-3 py-2.5 tabular-nums font-semibold">{formatNum(sumExact, 2)}</td>
          <td className="px-3 py-2.5 tabular-nums font-semibold">{sumRnd}</td>
          <td />
        </tr>
      </tfoot>
    );
  }
  if (kind === "grain") {
    const sumGrain = rows.reduce((s, { calc }) => s + calc.grainNeeded, 0);
    const sumOrder = rows.reduce((s, { order }) => s + order.order_quantity_kg, 0);
    return (
      <tfoot>
        <tr className="border-t-2 border-stone-200 bg-stone-50/80">
          <td className="px-3 py-2.5 font-semibold text-stone-700">Total</td>
          <td />
          <td className="px-3 py-2.5 tabular-nums font-semibold">{formatNum(sumOrder, 2)}</td>
          <td className="px-3 py-2.5 tabular-nums font-semibold">{formatNum(sumGrain, 2)} kg</td>
        </tr>
      </tfoot>
    );
  }
  return null;
}
